import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../api";
import { toasts } from "../components/AppToast";
import { useNavigate } from "react-router-dom";
import {
  FiGlobe,
  FiPlus,
  FiEye,
  FiEdit,
  FiTrash2,
  FiX,
  FiSearch,
} from "react-icons/fi";

export default function ManageSeo() {
  const navigate = useNavigate();

  // =========================================================
  // STATES
  // =========================================================

  const [data, setData] = useState([]);
  const [pages, setPages] = useState([]);
  const [languages, setLanguages] = useState([]);

  const [selectedPage, setSelectedPage] = useState("0");

  // English will be selected automatically
  const [selectedLang, setSelectedLang] = useState("");

  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const limit = 10;
  const [total, setTotal] = useState(0);

  // =========================================================
  // VIEW MODAL
  // =========================================================

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);

  // =========================================================
  // DELETE MODAL
  // =========================================================

  const [delModal, setDelModal] = useState(null);
  const [delSaving, setDelSaving] = useState(false);

  // =========================================================
  // FETCH LANGUAGES
  // =========================================================

  const fetchLanguages = async () => {
    try {
      const res = await API.get("/seo/languages");

      const languageList = res.data || [];

      setLanguages(languageList);

      // DEFAULT LANGUAGE = ENGLISH
      const english = languageList.find(
        (lang) =>
          String(lang.lang_name).trim().toLowerCase() === "english"
      );

      if (english) {
        setSelectedLang(String(english.lang_id));
      } else if (languageList.length > 0) {
        setSelectedLang(String(languageList[0].lang_id));
      }
    } catch (err) {
      console.error("LANGUAGE FETCH ERROR:", err);

      toasts.error(
        err.response?.data?.error || "Failed to load languages"
      );
    }
  };

  // =========================================================
  // FETCH PAGES
  // =========================================================

  const fetchPages = async () => {
    try {
      const res = await API.get("/seo/pages");

      setPages(res.data || []);
    } catch (err) {
      console.error("PAGE FETCH ERROR:", err);

      toasts.error(
        err.response?.data?.error || "Failed to load pages"
      );
    }
  };

  // =========================================================
  // FETCH SEO DATA
  // =========================================================

  const fetchData = useCallback(async () => {
    if (selectedLang === "") {
      return;
    }

    try {
      setLoading(true);

      const res = await API.get(
        `/seo?page=${page}` +
          `&limit=${limit}` +
          `&page_id=${selectedPage}` +
          `&lang_id=${selectedLang}`
      );

      setData(res.data?.data || []);

      setTotal(Number(res.data?.total) || 0);
    } catch (err) {
      console.error("SEO FETCH ERROR:", err);

      toasts.error(
        err.response?.data?.error || "Failed to load SEO data"
      );
    } finally {
      setLoading(false);
    }
  }, [page, selectedPage, selectedLang, limit]);

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    fetchLanguages();
    fetchPages();
  }, []);

  // =========================================================
  // LOAD SEO DATA
  // =========================================================

  useEffect(() => {
    if (selectedLang !== "") {
      fetchData();
    }
  }, [selectedLang, fetchData]);

  // =========================================================
  // PAGE FILTER
  // =========================================================

  const handlePageFilter = (e) => {
    setSelectedPage(e.target.value);
    setPage(1);
  };

  // =========================================================
  // LANGUAGE FILTER
  // =========================================================

  const handleLanguageFilter = (e) => {
    setSelectedLang(e.target.value);
    setPage(1);
  };

  // =========================================================
  // ADD
  // =========================================================

  const handleAdd = () => {
    navigate("/manage-seo/add");
  };

  // =========================================================
  // VIEW
  // =========================================================

  const handleView = async (row) => {
    try {
      const id = row.seodet_id ?? row.seo_id ?? row.id;

      if (!id) {
        toasts.error("SEO ID not found");
        return;
      }

      setLoading(true);

      const response = await API.get(`/seo/view/${id}`);

      if (!response.data?.success) {
        toasts.error(
          response.data?.message || "Failed to load SEO details"
        );
        return;
      }

      setViewData(response.data);
      setShowViewModal(true);
    } catch (error) {
      console.error("VIEW SEO ERROR:", error);

      toasts.error(
        error.response?.data?.message ||
          "Failed to load SEO details"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // CLOSE VIEW
  // =========================================================

  const handleCloseView = () => {
    setShowViewModal(false);
    setViewData(null);
  };

  // =========================================================
  // EDIT
  // =========================================================

  const handleEdit = (row) => {
    navigate("/manage-seo/edit", {
      state: {
        seo: row,
      },
    });
  };

  // =========================================================
  // DELETE MODAL OPEN
  // =========================================================

  const openDeleteModal = (row) => {
    setDelModal(row);
  };

  // =========================================================
  // CLOSE DELETE MODAL
  // =========================================================

  const closeDeleteModal = () => {
    if (delSaving) {
      return;
    }

    setDelModal(null);
  };

  // =========================================================
  // DELETE
  // =========================================================

  const handleDelete = async () => {
    if (!delModal) {
      return;
    }

    const id = delModal.seodet_id ?? delModal.seo_id ?? delModal.id;

    if (!id) {
      toasts.error("SEO ID not found");
      return;
    }

    try {
      setDelSaving(true);

      await API.delete(`/seo/${id}`);

      toasts.success("SEO deleted successfully");

      setDelModal(null);

      if (data.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        await fetchData();
      }
    } catch (err) {
      console.error("DELETE ERROR:", err);

      toasts.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to delete SEO"
      );
    } finally {
      setDelSaving(false);
    }
  };

  // =========================================================
  // PAGINATION
  // =========================================================

  const totalPages = Math.ceil(total / limit);

  const handlePrevious = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNext = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <Layout activePage="SEO">
    

      <main className="dashboard-main">
        <div className="table-card">

          {/* HEADER */}
          <div className="table-card-head">
            <h3 className="table-card-title d-flex align-items-center">
              <FiGlobe size={26} className="me-2" />
              <span>Manage SEO</span>
              <span className="rec-count ms-1">
                {total} records
              </span>
            </h3>

            <select
              className="form-select season-input"
              value={selectedPage}
              onChange={handlePageFilter}
            >
              <option value="0">— All Pages —</option>

              {pages.map((p, index) => (
                <option
                  key={`page-${p.page_id}-${index}`}
                  value={p.page_id}
                >
                  {p.page_alias}
                </option>
              ))}
            </select>

            <select
              className="form-select season-input"
              value={selectedLang}
              onChange={handleLanguageFilter}
            >
              {languages.map((lang, index) => (
                <option
                  key={`language-${lang.lang_id}-${index}`}
                  value={lang.lang_id}
                >
                  {lang.lang_name}
                </option>
              ))}
            </select>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="um-btn-primary"
                onClick={handleAdd}
              >
                <FiPlus className="me-1" />
                Add SEO
              </button>
            </div>
          </div>

          {/* TABLE */}
          <div>
            <table
              className="table table-striped table-hover table-bordered align-middle mb-0"
              style={{
                tableLayout: "fixed",
                width: "100%",
                fontSize: "12.5px",
              }}
            >
              <thead className="table-light">
                <tr className="um-table-head text-center">
                  <th
                    style={{ width: "3%" }}
                    className="text-center p-2"
                  >
                    #
                  </th>

                  <th
                    style={{ width: "15%" }}
                    className="text-center p-2"
                  >
                    Page
                  </th>

                  <th
                    style={{ width: "20%" }}
                    className="text-center p-2"
                  >
                    Meta Title
                  </th>

                  <th
                    style={{ width: "40%" }}
                    className="text-center p-2"
                  >
                    Key Word
                  </th>

                  <th
                    style={{ width: "10%" }}
                    className="text-center p-2"
                  >
                    Language
                  </th>

                  <th
                    style={{ width: "15%" }}
                    className="text-center p-2"
                  >
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {/* LOADING */}
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 6 }).map((_, j) => (
                        <td
                          key={j}
                          className="text-center p-2"
                        >
                          <div className="skeleton" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : data.length > 0 ? (
                  data.map((row, index) => (
                    <tr
                      key={
                        row.seodet_id ||
                        `${row.page_id}-${row.lang_id}-${index}`
                      }
                      className="um-row"
                    >
                      {/* NUMBER */}
                      <td className="text-center p-2 text-muted">
                        {(page - 1) * limit + index + 1}
                      </td>

                      {/* PAGE */}
                      <td
                        className="text-center p-2"
                        style={{ wordBreak: "break-word" }}
                      >
                        <strong>
                          {row.page_name ||
                            row.page_alias ||
                            "-"}
                        </strong>
                      </td>

                      {/* META TITLE */}
                      <td
                        className="text-center p-2"
                        style={{ wordBreak: "break-word" }}
                      >
                        {row.seo_metatitle || "-"}
                      </td>

                      {/* KEYWORD */}
                      <td
                        className="text-center p-2"
                        style={{ wordBreak: "break-word" }}
                      >
                        {row.seo_keyword || "-"}
                      </td>

                      {/* LANGUAGE */}
                      <td className="um-td text-center p-2">
                        <div className="status-wrap justify-content-center m-0">
                          <span
                            className="badge badge-success"
                            style={{ fontSize: "11px" }}
                          >
                            {row.lang_name || "-"}
                          </span>
                        </div>
                      </td>

                      {/* ACTION */}
                      <td className="p-2 text-center">
                        <div className="action-buttons d-flex justify-content-center gap-1">

                          {/* VIEW */}
                          <button
                            type="button"
                            className="action-btn action-view btn btn-sm p-1"
                            onClick={() => handleView(row)}
                            title="View"
                          >
                            <FiEye size={13} />
                          </button>

                          {/* EDIT */}
                          <button
                            type="button"
                            className="action-btn action-edit btn btn-sm p-1"
                            onClick={() => handleEdit(row)}
                            title="Edit"
                          >
                            <FiEdit size={13} />
                          </button>

                          {/* DELETE */}
                          <button
                            type="button"
                            className="action-btn action-delete btn btn-sm p-1"
                            onClick={() => openDeleteModal(row)}
                            title="Delete"
                          >
                            <FiTrash2 size={13} />
                          </button>

                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="6"
                      className="text-center py-5"
                    >
                      <div className="empty-icon">
                        <FiSearch size={22} />
                      </div>

                      <div className="empty-title">
                        No records Available
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          {total > 0 && (
            <div className="pagination">
              <span className="pagin-info">
                Showing {(page - 1) * limit + 1} –{" "}
                {Math.min(page * limit, total)} of {total} records
              </span>

              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="pg-btn"
                  disabled={page === 1 || loading}
                  onClick={handlePrevious}
                >
                  ← Prev
                </button>

                <span className="pagin-info">
                  Page {page} / {totalPages || 1}
                </span>

                <button
                  type="button"
                  className="pg-btn"
                  disabled={
                    page === totalPages ||
                    totalPages === 0 ||
                    loading
                  }
                  onClick={handleNext}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* =====================================================
          VIEW MODAL
          ===================================================== */}

      {showViewModal && viewData && (
        <div
          className="um-overlay"
          onClick={handleCloseView}
        >
          <div
            className="um-modal um-view-modal modal-lg"
            onClick={(e) => e.stopPropagation()}
          >

            {/* HEADER */}
            <div className="um-modal-head">
              <h2 className="um-modal-title">
                SEO Details
              </h2>

              <button
                type="button"
                className="um-x-btn"
                onClick={handleCloseView}
              >
                <FiX />
              </button>
            </div>

            {/* BODY */}
            <div className="um-modal-scroll">

              <div className="sec-div">
                <span>
                  PAGE & LANGUAGE INFORMATION
                </span>
              </div>

              <div className="row g-3">

                <div className="col-md-6">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">
                        Page
                      </div>

                      <div className="profile-row-value">
                        {viewData.page_name ||
                          viewData.page_alias ||
                          "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">
                        Language
                      </div>

                      <div className="profile-row-value">
                        {viewData.lang_name || "—"}
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              <div className="sec-div mt-4">
                <span>
                  SEO PARAMETERS
                </span>
              </div>

              <div className="row g-3">
                {viewData.seo_parameters &&
                viewData.seo_parameters.length > 0 ? (
                  viewData.seo_parameters.map(
                    (parameter, index) => {
                      const parameterId = Number(
                        parameter.seo_parmid
                      );

                      const parameterName =
                        parameter.parameter_name ||
                        `SEO Parameter ${index + 1}`;

                      const parameterValue =
                        parameter.seo_details || "—";

                      const isTextarea =
                        parameterId === 2;

                      return (
                        <div
                          className="col-12"
                          key={
                            parameter.seo_parmid ||
                            index
                          }
                        >
                          <div className="profile-row">
                            <div className="w-100">

                              <div className="form-label">
                                {parameterName}
                              </div>

                              {isTextarea ? (
                                <textarea
                                  className="form-control mt-1"
                                  rows="3"
                                  value={
                                    parameter.seo_details ||
                                    ""
                                  }
                                  readOnly
                                />
                              ) : (
                                <div className="profile-row-value">
                                  {parameterValue}
                                </div>
                              )}

                            </div>
                          </div>
                        </div>
                      );
                    }
                  )
                ) : (
                  <div className="col-12">
                    <div className="text-muted p-2">
                      No SEO parameters found.
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* FOOTER */}
            <div className="um-modal-foot">

              <button
                type="button"
                className="um-btn-secondary"
                onClick={handleCloseView}
              >
                Close
              </button>

              <button
                type="button"
                className="um-btn-primary"
                onClick={() => {
                  handleCloseView();
                  handleEdit(viewData);
                }}
              >
                <FiEdit className="me-1" />
                Edit
              </button>

            </div>
          </div>
        </div>
      )}

      {/* =====================================================
          DELETE MODULE MODAL
          ===================================================== */}

      {delModal && (
        <div
          className="um-overlay"
          onClick={closeDeleteModal}
        >
          <div
            className="um-modal um-delete-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="delete-content">

              {/* DELETE ICON */}
              <div className="delete-icon">
                <FiTrash2 size={32} />
              </div>

              {/* DELETE TITLE */}
              <h2 className="delete-title">
                Delete Seo?
              </h2>

              {/* DELETE DESCRIPTION */}
              <p className="delete-description">
                Are you sure you want to delete{" "}
                <span className="badge badge-success">
                  "
                  {delModal.page_name ||
                    delModal.page_alias ||
                    "this module"}
                  "
                </span>
                ?
                <br />

                This will permanently remove this module.
                <br />

                <strong>
                  This action cannot be undone.
                </strong>
              </p>

              {/* DELETE ACTIONS */}
              <div className="delete-actions">

                {/* CANCEL */}
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={closeDeleteModal}
                  disabled={delSaving}
                >
                  Cancel
                </button>

                {/* CONFIRM DELETE */}
                <button
                  type="button"
                  disabled={delSaving}
                  className="delete-confirm-btn"
                  onClick={handleDelete}
                >
                  {delSaving ? (
                    <span className="spinner" />
                  ) : (
                    <>
                      <FiTrash2 />
                      Delete Permanently
                    </>
                  )}
                </button>

              </div>
            </div>
          </div>
        </div>
      )}
       
    </Layout>
  );
}